import type {
  BBox,
  BuildProgress,
  LatLon,
  LobbyMode,
  LobbyPublic,
  RegionRequest,
  Tour,
  TourSummary,
  WeatherChoice,
  WeatherKind,
} from '../../shared/types.js';
import { bboxAreaKm2 } from '../../shared/projection.js';
import { num, plural, serverText, t } from '../i18n/i18n.js';
import { clear, el, hex, icon } from './dom.js';
import { BuildClock, formatElapsed, remainingPhrase } from './buildClock.js';
import { nudge } from './steps.js';
import { previewSvg } from './tourPreview.js';
import type { IconName } from './icons.js';
import { forgetRegion, sameRegion, type SavedRegion } from '../savedRegions.js';

/**
 * What the lobby is for, as the host's first choice. Two of these are the server's `mode`;
 * `tour` is a race with a cup picked, and exists on this screen before one is — the host has
 * said "a tour" and is looking through them.
 */
type Intent = 'race' | 'tour' | 'free';

export interface FixtureInfo {
  id: string;
  title: string;
  blurb: string;
  bbox: BBox;
  start: LatLon;
  finish: LatLon;
  areaKm2: number;
}

export const CAR_COLORS = [
  0xe8503a, 0xffb020, 0x4cc38a, 0x39a0ff, 0xa06cff, 0xff6bb5, 0xf2f2f2, 0x2c333d,
];

// ---------------------------------------------------------------------------
// Lobby
// ---------------------------------------------------------------------------

/**
 * Cars on the grid, which is also the lobby's player cap. Mirrors `MAX_PLAYERS` on the
 * server; the client only needs it to know how many opponent chips to offer, and the
 * server clamps the count it is sent regardless.
 */
const MAX_CARS = 8;

export interface LobbyScreenOptions {
  onReady(ready: boolean): void;
  /** Host: race or drive here. `title` is what the lobby calls it, for a stop or a saved drawing. */
  onRegion(region: RegionRequest, title?: string): void;
  /**
   * Host: open the race wizard, on a blank map or on the race already chosen. The shell owns
   * the wizard — the tour builder uses the same one — and sends whatever comes back.
   */
  onDraw(initial: RegionRequest | null): void;
  onAiCount(count: number): void;
  /** Host: race the region, or drive round it with no gates. */
  onMode(mode: LobbyMode): void;
  /** Host: the real weather and time, or a chosen one. */
  onWeatherChoice(choice: WeatherChoice): void;
  onStart(): void;
  onLeave(): void;
  /** Host: run a saved cup, or null to go back to a single race. */
  onTour(tourId: string | null): void;
  /** Host: open the builder, either blank or on an existing tour. */
  onEditTour(tourId: string | null): void;
  /** Host: stop waiting on a world that is taking too long. */
  onCancelGeneration(): void;
}

export class LobbyScreen {
  readonly root: HTMLElement;
  private readonly codeNode: HTMLElement;
  private readonly playerList: HTMLElement;
  private readonly regionNote: HTMLElement;
  private readonly startButton: HTMLButtonElement;
  /** Why Start did nothing, said when it is pressed rather than by greying it out. */
  private readonly startNote: HTMLElement;
  private readonly cancelButton: HTMLButtonElement;
  private readonly readyButton: HTMLButtonElement;
  private readonly hostPanel: HTMLElement;
  /** A guest's view of the host's choice, above the same panel the host uses. */
  private readonly watchPanel: HTMLElement;
  private readonly watchTitle: HTMLElement;
  private readonly watchDetail: HTMLElement;
  private readonly fixtureList: HTMLElement;
  private readonly drawCard: HTMLButtonElement;
  /** The host's own drawn region, shown as a card of its own once there is one. */
  private readonly customCard: HTMLButtonElement;
  private readonly customDetail: HTMLElement;
  private readonly adjustButton: HTMLButtonElement;
  private readonly adjustNote = el('p', { class: 'field-support', role: 'status' });
  /** The shell parks the account and leaderboard pills here while this screen is up. */
  readonly badgeSlot = el('div', { class: 'lobby-badges' });
  private readonly errorNode: HTMLElement;
  private readonly copyButton: HTMLButtonElement;
  private readonly aiChips: HTMLButtonElement[] = [];
  private readonly aiNote: HTMLElement;
  private readonly tourList: HTMLElement;
  private readonly tourNote: HTMLElement;
  private readonly tourSearch: HTMLInputElement;
  private readonly tourFilter: HTMLElement;
  private tourFilterMode: 'all' | 'mine' = 'all';
  private readonly intentCards: Record<Intent, HTMLButtonElement>;
  /** The host pressed "Tour" and has not picked one yet: nothing on the server says so. */
  private choosingTour = false;
  private intent: Intent = 'race';
  private readonly tourSection: HTMLElement;
  private readonly placesTitle: HTMLElement;
  private readonly savedHead: HTMLElement;
  private readonly savedList: HTMLElement;
  private readonly stopsHead: HTMLElement;
  private readonly stopsList: HTMLElement;
  private savedRegions: SavedRegion[] = [];
  private tourDetails: Tour[] = [];
  /** Opponents have nothing to do in a free drive, so the section goes with it. */
  private readonly aiSection: HTMLElement;
  private readonly weatherButtons: { real: HTMLButtonElement; chosen: HTMLButtonElement };
  private readonly weatherCustom: HTMLElement;
  private readonly weatherHour: HTMLInputElement;
  private readonly weatherClock: HTMLElement;
  private readonly weatherKinds = new Map<WeatherKind, HTMLButtonElement>();
  /** The host's slider while it is being dragged, so a broadcast does not snap it back. */
  private draggingHour = false;
  private readonly regionSection: HTMLElement;
  private readonly buildPanel: HTMLElement;
  private readonly buildLabel: HTMLElement;
  private readonly buildDetail: HTMLElement;
  private readonly buildFill: HTMLElement;
  private readonly buildElapsed: HTMLElement;
  private readonly buildNote: HTMLElement;
  private readonly buildLeft: HTMLElement;
  private buildTimer: number | null = null;
  private readonly buildClock = new BuildClock();

  private readonly chatSlot = el('div', { class: 'lobby-chat-slot' });
  private lobby: LobbyPublic | null = null;
  private localId = '';
  private isHost = false;
  private selfReady = false;
  private fixtures: FixtureInfo[] = [];
  private tours: TourSummary[] = [];
  private selectedTourId: string | null = null;

  constructor(private readonly options: LobbyScreenOptions) {
    this.codeNode = el('span', { class: 'code-value', id: 'lobby-code-value' }, '----');
    this.copyButton = el('button', { class: 'button button-tonal', type: 'button' }, icon('link'), t('Copy link'));
    this.copyButton.addEventListener('click', () => this.copyLink());

    this.playerList = el('ul', { class: 'player-list', id: 'player-list' });
    this.regionNote = el('p', { class: 'region-note' }, t('The host has not picked a region yet.'));
    this.errorNode = el('p', { class: 'form-error', id: 'lobby-error' });

    this.fixtureList = el('div', { class: 'fixture-list' });

    // Drawing a region is a card among the regions rather than a toggle under them, because
    // it is the same kind of choice — where are we racing — and the map it opens now takes
    // the whole screen rather than unfolding in the middle of this panel.
    this.drawCard = el(
      'button',
      { class: 'fixture is-action host-only', type: 'button', id: 'custom-region-toggle' },
      icon('draw', 'fixture-icon'),
      el('span', { class: 'fixture-title' }, t('Draw your own')),
      el('span', { class: 'fixture-blurb' }, t('Any streets on Earth, step by step')),
    );
    this.drawCard.addEventListener('click', () => {
      if (this.isHost) this.options.onDraw(null);
    });
    this.customDetail = el('span', { class: 'fixture-tag' });
    this.customCard = el(
      'button',
      { class: 'fixture is-custom is-hidden', type: 'button', id: 'custom-region-card' },
      icon('map', 'fixture-icon'),
      el('span', { class: 'fixture-title' }, t('Your own region')),
      this.customDetail,
    );
    this.customCard.addEventListener('click', () => {
      if (this.isHost && this.lobby?.region && !this.lobby.region.fixture) this.options.onDraw(this.lobby.region);
    });

    this.adjustButton = el(
      'button',
      { class: 'button button-ghost host-only', type: 'button', id: 'region-adjust' },
      icon('route'),
      t('Adjust the route'),
    );
    this.adjustButton.addEventListener('click', () => {
      const region = this.lobby?.region;
      if (!region) {
        this.say(
          this.adjustNote,
          t('Pick a region first — then you can move its start and finish, turn the grid round or place checkpoints.'),
        );
        return;
      }
      this.options.onDraw(region);
    });

    // One chip per possible field size, zero through a full grid. A count is a small set of
    // discrete choices you pick once, so it gets the picker's chip row rather than a
    // stepper — the whole range is visible and reachable in one click.
    const aiChipRow = el('div', { class: 'chip-row', id: 'ai-count' });
    for (let count = 0; count < MAX_CARS; count++) {
      const chip = el(
        'button',
        { class: 'chip', type: 'button', dataset: { aiCount: String(count) } },
        String(count),
      );
      chip.addEventListener('click', () => {
        if (this.isHost) this.options.onAiCount(count);
      });
      this.aiChips.push(chip);
      aiChipRow.append(chip);
    }
    this.aiNote = el('p', { class: 'panel-hint' }, '');

    // --- tours -------------------------------------------------------------
    this.tourList = el('div', { class: 'tour-cards', id: 'tour-list' });
    this.tourSearch = el('input', {
      class: 'field field-search',
      type: 'search',
      id: 'tour-search',
      placeholder: t('Search tours'),
      autocomplete: 'off',
    });
    this.tourSearch.addEventListener('input', () => this.renderTourCards());
    this.tourFilter = el('div', { class: 'chip-row tour-filter host-only' });
    for (const [key, label] of [
      ['all', t('All')],
      ['mine', t('Mine')],
    ] as const) {
      const chip = el(
        'button',
        { class: `chip${key === 'all' ? ' is-active' : ''}`, type: 'button', dataset: { tourFilter: key } },
        label,
      );
      chip.addEventListener('click', () => {
        this.tourFilterMode = key;
        for (const other of this.tourFilter.querySelectorAll('.chip')) {
          other.classList.toggle('is-active', other === chip);
        }
        this.renderTourCards();
      });
      this.tourFilter.append(chip);
    }
    this.tourNote = el('p', { class: 'panel-hint' }, '');

    // The first choice, on its own row: what kind of evening this is. It used to share a row
    // with the saved tours, where "Free drive" read as one more map rather than as a way of
    // using any of them — and nobody could tell at a glance which of the three was picked.
    const intentCard = (intent: Intent, title: string, sub: string, glyph: IconName) => {
      const card = el(
        'button',
        { class: 'intent-card', type: 'button', id: `intent-${intent}`, role: 'radio' },
        icon(glyph, 'intent-icon'),
        el('span', { class: 'intent-title' }, title),
        el('span', { class: 'intent-sub' }, sub),
        icon('check_circle', 'intent-check'),
      );
      card.addEventListener('click', () => this.chooseIntent(intent));
      return card;
    };
    this.intentCards = {
      race: intentCard('race', t('Race'), t('One region, first to the flag wins'), 'sports_score'),
      tour: intentCard('tour', t('Tour'), t('Several races back to back, for points'), 'trophy'),
      free: intentCard('free', t('Free drive'), t('No gates, no clock — drop in any time'), 'explore'),
    };

    this.savedHead = el('h4', { class: 'places-head' }, t('Saved in this browser'));
    this.savedList = el('div', { class: 'tour-cards places-list', id: 'saved-regions' });
    this.stopsHead = el('h4', { class: 'places-head' }, t('From the tours'));
    this.stopsList = el('div', { class: 'places-tours', id: 'places-stops' });
    this.placesTitle = el('h3', { class: 'panel-title' }, t('Where are we racing?'));

    const newTourButton = el(
      'button',
      { class: 'button button-ghost host-only', type: 'button', id: 'tour-new' },
      icon('add'),
      t('New tour'),
    );
    newTourButton.addEventListener('click', () => this.options.onEditTour(null));

    this.regionSection = el(
      'div',
      { class: 'host-section', id: 'places' },
      el('p', { class: 'step-label' }, t('Step 2')),
      this.placesTitle,
      el('h4', { class: 'places-head' }, t('Built in, or drawn by you')),
      this.fixtureList,
      el('div', { class: 'host-actions host-only' }, this.adjustButton, this.adjustNote),
      this.savedHead,
      this.savedList,
      this.stopsHead,
      this.stopsList,
    );

    // What a guest sees above the picker: the host's choice in a sentence, so it reads even
    // when the thing picked is not on this guest's shelf — a tour saved after they loaded the
    // page, a region drawn by hand.
    this.watchTitle = el('p', { class: 'watch-title', id: 'watch-title' }, '');
    this.watchDetail = el('p', { class: 'watch-detail', id: 'watch-detail' }, '');
    this.watchPanel = el(
      'div',
      { class: 'watch-panel is-hidden', id: 'watch-panel' },
      el('span', { class: 'caption' }, t('The host’s pick')),
      this.watchTitle,
      this.watchDetail,
    );

    this.tourSection = el(
      'div',
      { class: 'host-section', id: 'tours' },
      el('p', { class: 'step-label' }, t('Step 2')),
      el('div', { class: 'tour-head' }, el('h3', { class: 'panel-title' }, t('Which tour?')), newTourButton),
      el('div', { class: 'tour-browse-bar host-only' }, this.tourSearch, this.tourFilter),
      el('div', { class: 'tour-cards-row' }, this.tourList),
      this.tourNote,
    );

    // --- weather and time ---------------------------------------------------
    // The real sky is the default, and the point: you race the place as it is right now. The
    // other choice is a time and a sky of the host's — a night race at lunchtime.
    const weatherSegment = (id: string, label: string, glyph: IconName, pick: () => void) => {
      const button = el(
        'button',
        { class: 'segment', type: 'button', id },
        icon(glyph, 'segment-icon'),
        el('span', { class: 'segment-label' }, label),
      );
      button.addEventListener('click', () => {
        if (this.isHost) pick();
      });
      return button;
    };
    this.weatherButtons = {
      real: weatherSegment('weather-real', t('Real, right now'), 'my_location', () =>
        this.options.onWeatherChoice({ real: true }),
      ),
      chosen: weatherSegment('weather-chosen', t('Choose'), 'schedule', () => this.sendWeather({})),
    };
    this.weatherHour = el('input', {
      class: 'weather-hour',
      type: 'range',
      id: 'weather-hour',
      min: '0',
      max: '23.75',
      step: '0.25',
    });
    this.weatherClock = el('span', { class: 'setting-value tabular', id: 'weather-clock' }, '');
    // Shown while dragging, sent on release: one choice, not forty broadcasts.
    this.weatherHour.addEventListener('input', () => {
      this.draggingHour = true;
      this.weatherClock.textContent = clockOf(Number(this.weatherHour.value));
    });
    this.weatherHour.addEventListener('change', () => {
      this.draggingHour = false;
      this.sendWeather({ hour: Number(this.weatherHour.value) });
    });
    const kinds = el('div', { class: 'chip-row', id: 'weather-kinds' });
    for (const [kind, label] of [
      ['clear', t('Clear')],
      ['cloudy', t('Partly cloudy')],
      ['overcast', t('Overcast')],
      ['fog', t('Fog')],
      ['rain', t('Rain')],
      ['storm', t('Thunderstorm')],
      ['snow', t('Snow')],
    ] as const) {
      const chip = el('button', { class: 'chip', type: 'button', dataset: { weatherKind: kind } }, label);
      chip.addEventListener('click', () => {
        if (this.isHost) this.sendWeather({ kind });
      });
      this.weatherKinds.set(kind, chip);
      kinds.append(chip);
    }
    this.weatherCustom = el(
      'div',
      { class: 'weather-custom is-hidden' },
      el('label', { class: 'setting-row', htmlFor: 'weather-hour' }, el('span', { class: 'setting-title' }, t('Local time')), this.weatherClock),
      this.weatherHour,
      kinds,
    );
    const weatherSection = el(
      'div',
      { class: 'host-section', id: 'weather' },
      el('h3', { class: 'panel-title' }, t('Weather and time')),
      el('div', { class: 'segmented', role: 'group' }, this.weatherButtons.real, this.weatherButtons.chosen),
      this.weatherCustom,
    );

    this.hostPanel = el(
      'div',
      { class: 'host-panel' },
      el(
        'div',
        { class: 'host-section' },
        el('p', { class: 'step-label' }, t('Step 1')),
        el('h3', { class: 'panel-title' }, t('What are we doing?')),
        el(
          'div',
          { class: 'intent-row', role: 'radiogroup' },
          this.intentCards.race,
          this.intentCards.tour,
          this.intentCards.free,
        ),
      ),
      this.tourSection,
      this.regionSection,
      (this.aiSection = el(
        'div',
        { class: 'host-section' },
        el('h3', { class: 'panel-title' }, t('Opponents')),
        aiChipRow,
        this.aiNote,
      )),
      weatherSection,
    );

    this.readyButton = el('button', { class: 'button button-tonal', type: 'button', id: 'ready-button' }, t("I'm ready"));
    this.readyButton.addEventListener('click', () => {
      this.selfReady = !this.selfReady;
      this.options.onReady(this.selfReady);
    });

    this.startButton = el(
      'button',
      { class: 'button button-primary', type: 'button', id: 'start-button' },
      icon('play_arrow'),
      t('Start race'),
    );
    this.startNote = el('p', { class: 'field-support start-note', id: 'start-note', role: 'status' });
    // Never disabled. Pressed too early it says what is missing — a greyed-out Start was the
    // single most common "why can't I…?" in the lobby, and it could not answer.
    this.startButton.addEventListener('click', () => {
      const reason = this.startBlocker();
      if (reason) {
        this.say(this.startNote, reason);
        return;
      }
      this.startNote.textContent = '';
      this.options.onStart();
    });

    // Only ever visible while a world is building. A custom region in a city nobody has
    // cached is minutes of Overpass, and until this existed the only way out of that wait
    // was to restart the server.
    this.cancelButton = el(
      'button',
      { class: 'button button-ghost is-hidden', type: 'button', id: 'cancel-generation' },
      t('Stop building'),
    );
    this.cancelButton.addEventListener('click', () => this.options.onCancelGeneration());

    const leaveButton = el('button', { class: 'button button-ghost', type: 'button' }, icon('logout'), t('Leave'));
    leaveButton.addEventListener('click', () => this.options.onLeave());

    // The build panel. Everyone in the lobby sees it, not just the host — the guests were
    // the ones with the least idea what was happening, since their screen said nothing at
    // all while the host's at least had a disabled button on it.
    this.buildLabel = el('p', { class: 'build-label' }, t('Building the world'));
    this.buildDetail = el('p', { class: 'build-detail' }, '');
    this.buildElapsed = el('span', { class: 'build-elapsed tabular' }, formatElapsed(0));
    this.buildFill = el('div', { class: 'build-bar-fill' });
    this.buildNote = el('p', { class: 'build-note' }, '');
    this.buildLeft = el('p', { class: 'build-clock', id: 'build-left' }, '');
    this.buildPanel = el(
      'div',
      { class: 'build-panel is-hidden', id: 'build-panel' },
      el('div', { class: 'build-head' }, this.buildLabel, this.buildElapsed),
      el('div', { class: 'build-bar' }, this.buildFill),
      this.buildDetail,
      this.buildLeft,
      this.buildNote,
    );

    // The lobby is a second sheet of the same roadbook: the code is the reference number in
    // the corner, the header says what is being raced, and the two columns below it split
    // "who is here" from "what we are doing about it". It runs the full width of the screen
    // because the old one was a 380px column with a kilometre of empty page beside it.
    this.root = el(
      'div',
      { class: 'screen screen-lobby' },
      el(
        'div',
        { class: 'lobby-sheet' },
        el(
          'header',
          { class: 'lobby-head' },
          el(
            'div',
            { class: 'lobby-ref' },
            el('span', { class: 'caption' }, t('Lobby code')),
            this.codeNode,
          ),
          el(
            'div',
            { class: 'lobby-head-what' },
            el('h2', { class: 'lobby-title' }, t('Race control')),
            this.regionNote,
          ),
          el('div', { class: 'lobby-head-tools' }, this.badgeSlot, this.copyButton, leaveButton),
        ),
        el(
          'div',
          { class: 'lobby-grid' },
          el(
            'section',
            { class: 'lobby-col lobby-col-side' },
            el('h3', { class: 'panel-title' }, t('Drivers')),
            this.playerList,
            this.buildPanel,
            this.errorNode,
            el(
              'div',
              { class: 'lobby-actions' },
              this.readyButton,
              this.startButton,
              this.cancelButton,
            ),
            this.startNote,
            // Filled by the shell, which owns the socket the messages travel on. A slot
            // rather than a constructor argument so the lobby keeps knowing nothing about
            // chat beyond where it goes.
            this.chatSlot,
          ),
          el('section', { class: 'lobby-col lobby-col-main' }, this.watchPanel, this.hostPanel),
        ),
      ),
    );
  }

  /** Puts the shell's chat panel in the lobby column, under the ready buttons. */
  attachChat(panel: HTMLElement): void {
    clear(this.chatSlot);
    this.chatSlot.append(panel);
  }

  setFixtures(fixtures: FixtureInfo[], maxAreaKm2: number): void {
    this.fixtures = fixtures;
    void maxAreaKm2;
    clear(this.fixtureList);
    for (const fixture of fixtures) {
      const button = el(
        'button',
        { class: 'fixture', type: 'button', dataset: { fixture: fixture.id } },
        icon('map', 'fixture-icon'),
        el('span', { class: 'fixture-title' }, serverText(fixture.title)),
        el('span', { class: 'fixture-blurb' }, serverText(fixture.blurb)),
        el('span', { class: 'fixture-tag' }, `${num(fixture.areaKm2)} km²`),
      );
      button.addEventListener('click', () => {
        if (!this.isHost) return;
        for (const other of this.fixtureList.querySelectorAll('.fixture')) {
          other.classList.toggle('is-active', other === button);
        }
        this.options.onRegion({ fixture: fixture.id, bbox: fixture.bbox, seed: 1 });
      });
      this.fixtureList.append(button);
    }
    this.fixtureList.append(this.customCard, this.drawCard);
  }

  setTours(tours: TourSummary[]): void {
    this.tours = tours;
    this.renderTourCards();
  }

  /**
   * Matches a tour against the search box.
   *
   * Names, the person who made it, and the races inside it — searching "Kazimierz" should
   * find the cup that visits it even though the cup is called something else entirely.
   * Folded and accent-stripped, so "krakow" finds "Kraków".
   */
  private matches(tour: TourSummary, query: string): boolean {
    if (!query) return true;
    const haystack = fold(
      [tour.name, tour.owner ?? tour.author, ...tour.stopTitles].filter(Boolean).join(' '),
    );
    return query.split(/\s+/).every((word) => haystack.includes(word));
  }

  private renderTourCards(): void {
    const query = fold(this.tourSearch.value.trim());
    const visible = this.tours.filter(
      (tour) => (this.tourFilterMode === 'all' || tour.mine) && this.matches(tour, query),
    );

    clear(this.tourList);
    for (const tour of visible) {
      const card = el(
        'button',
        { class: 'tour-card', type: 'button', dataset: { tour: tour.id } },
        // The sketch, drawn in the browser from lines stored with the tour. No image is
        // fetched and the server does no work at all — see `tourPreview.ts`.
        el('span', { class: 'tour-card-art' }, previewSvg(tour.preview)),
        el('span', { class: 'tour-card-name' }, tour.name),
        el(
          'span',
          { class: 'tour-card-sub' },
          plural(tour.stopCount, '{n} race', '{n} races'),
          tour.owner ? ` · ${tour.owner}` : tour.author ? ` · ${tour.author}` : '',
        ),
        el('span', { class: 'tour-card-stops' }, tour.stopTitles.join(' → ')),
        // Read-only here: the lobby is for picking, and voting is on the front door's cards.
        el(
          'span',
          { class: 'tour-card-votes' },
          icon('thumb_up'),
          num(tour.up),
          icon('thumb_down'),
          num(tour.down),
        ),
        // Shipped with the server, or made before accounts existed: raceable, and nobody's
        // to change.
        tour.frozen && el('span', { class: 'tour-card-badge' }, tour.curated ? t('curated') : t('archived')),
      );
      card.addEventListener('click', () => {
        if (this.isHost) this.options.onTour(tour.id);
      });

      // A pencil inside a button inside a button is not legal HTML, so the edit affordance
      // is a sibling overlaid on the card rather than a child of it. It only appears on the
      // caller's own tours, because it is the only case the server will accept.
      const wrap = el('div', { class: 'tour-card-wrap' }, card);
      if (tour.mine) {
        const edit = el(
          'button',
          {
            class: 'tour-card-edit host-only',
            type: 'button',
            title: t('Edit {name}', { name: tour.name }),
            dataset: { tourEdit: tour.id },
          },
          '✎',
        );
        edit.addEventListener('click', (event) => {
          event.stopPropagation();
          this.options.onEditTour(tour.id);
        });
        wrap.append(edit);
      }
      this.tourList.append(wrap);
    }

    if (!visible.length && this.tours.length) {
      this.tourList.append(el('p', { class: 'panel-hint tour-empty' }, t('Nothing matches that.')));
    }
    this.paintTourSelection();
  }

  private paintTourSelection(): void {
    for (const card of this.tourList.querySelectorAll('.tour-card')) {
      card.classList.toggle('is-active', (card as HTMLElement).dataset.tour === this.selectedTourId);
    }
    const selected = this.tours.find((tour) => tour.id === this.selectedTourId);
    this.tourNote.textContent = selected
      ? `${selected.name}: ${selected.stopTitles.join(' → ')}`
      : this.tours.length
        ? t('Pick a tour to race the whole cup.')
        : t('No tours saved yet. Make one and it is there for everybody.');
  }

  /**
   * The host's first choice. Race and free drive are the server's mode; a tour is a race
   * with a cup on it, and until one is picked only this screen knows the host is choosing.
   */
  private chooseIntent(intent: Intent): void {
    if (!this.isHost || !this.lobby) return;
    const lobby = this.lobby;
    this.choosingTour = intent === 'tour' && !lobby.tour;
    if (intent === 'free') {
      if (lobby.mode !== 'free') this.options.onMode('free');
    } else {
      if (lobby.mode !== 'race') this.options.onMode('race');
      // A single race drops the cup; choosing "Tour" keeps one that is already picked.
      if (intent === 'race' && lobby.tour) this.options.onTour(null);
    }
    this.update(lobby, this.localId);
  }

  /** Which of the three the lobby is doing, from the server's state and the host's pending pick. */
  private intentOf(lobby: LobbyPublic): Intent {
    if (lobby.mode === 'free') return 'free';
    if (lobby.tour) return 'tour';
    return this.choosingTour ? 'tour' : 'race';
  }

  /** What this browser has drawn and kept. See `savedRegions.ts`. */
  setSavedRegions(regions: SavedRegion[]): void {
    this.savedRegions = regions;
    this.renderPlaces();
  }

  /** Every tour in full, for its stops to be offered as places to race or drive. */
  setTourDetails(tours: Tour[]): void {
    this.tourDetails = tours;
    this.renderPlaces();
  }

  /**
   * The places beyond the built-in four: the regions drawn in this browser, and every race of
   * every tour, each one pickable on its own. A tour's stops used to be reachable only by
   * running the whole cup.
   */
  private renderPlaces(): void {
    const place = (title: string, sub: string, preview: SavedRegion['preview'], pick: () => void, key: string) => {
      const card = el(
        'button',
        { class: 'tour-card place-card', type: 'button', dataset: { place: key } },
        el('span', { class: 'tour-card-art' }, previewSvg(preview)),
        el('span', { class: 'tour-card-name' }, title),
        el('span', { class: 'tour-card-sub' }, sub),
      );
      card.addEventListener('click', () => {
        if (this.isHost) pick();
      });
      return card;
    };

    clear(this.savedList);
    for (const saved of this.savedRegions) {
      const card = place(
        saved.title,
        t('{area} km² · drawn here', { area: num(bboxAreaKm2(saved.region.bbox), 2) }),
        saved.preview,
        () => this.options.onRegion(saved.region, saved.title),
        `saved:${saved.id}`,
      );
      const forget = el(
        'button',
        { class: 'tour-card-edit host-only', type: 'button', title: t('Forget {name}', { name: saved.title }) },
        icon('delete'),
      );
      forget.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setSavedRegions(forgetRegion(saved.id));
      });
      this.savedList.append(el('div', { class: 'tour-card-wrap' }, card, forget));
    }
    this.savedHead.classList.toggle('is-hidden', this.savedRegions.length === 0);
    this.savedList.classList.toggle('is-hidden', this.savedRegions.length === 0);

    clear(this.stopsList);
    for (const tour of this.tourDetails) {
      const cards = el('div', { class: 'tour-cards places-list' });
      tour.stops.forEach((stop, i) => {
        cards.append(
          place(
            stop.title,
            tour.name,
            tour.preview?.[i],
            () => this.options.onRegion(stop.region, stop.title),
            `stop:${tour.id}:${stop.id}`,
          ),
        );
      });
      // In a free drive a whole tour is a playlist: the host moves everyone to the next map
      // from the Esc menu. In a race, running the whole tour is what "Tour" above is for.
      const whole = el(
        'button',
        { class: 'button button-ghost host-only drive-tour', type: 'button', dataset: { driveTour: tour.id } },
        icon('route'),
        t('Drive them all, one after another'),
      );
      whole.addEventListener('click', () => {
        if (this.isHost) this.options.onTour(tour.id);
      });
      this.stopsList.append(
        el(
          'div',
          { class: 'places-tour', dataset: { tour: tour.id } },
          el('div', { class: 'places-tour-head' }, el('span', { class: 'places-tour-name' }, tour.name), whole),
          cards,
        ),
      );
    }
    this.stopsHead.classList.toggle('is-hidden', this.tourDetails.length === 0);
    if (this.lobby) this.paintPlaces(this.lobby);
  }

  /** Lights up whichever place the lobby is on — following the server, not the click. */
  private paintPlaces(lobby: LobbyPublic): void {
    const region = lobby.region;
    let matched = false;
    for (const saved of this.savedRegions) {
      const on = !lobby.tour && sameRegion(region, saved.region);
      matched ||= on;
      this.savedList.querySelector(`[data-place="saved:${saved.id}"]`)?.classList.toggle('is-active', on);
    }
    for (const tour of this.tourDetails) {
      const playlist = lobby.mode === 'free' && lobby.tour?.id === tour.id;
      const group = this.stopsList.querySelector<HTMLElement>(`.places-tour[data-tour="${tour.id}"]`);
      group?.classList.toggle('is-active', playlist);
      for (const stop of tour.stops) {
        const on = playlist
          ? lobby.tour!.stops[lobby.tour!.index]?.id === stop.id
          : !lobby.tour && sameRegion(region, stop.region);
        matched ||= on;
        group?.querySelector(`[data-place="stop:${tour.id}:${stop.id}"]`)?.classList.toggle('is-active', on);
      }
    }
    for (const button of this.stopsList.querySelectorAll<HTMLElement>('.drive-tour')) {
      button.hidden = lobby.mode !== 'free';
    }
    // The drawn region's own card is only for one that is not already on a card above.
    const custom = region && !region.fixture && !matched ? region : null;
    this.customCard.classList.toggle('is-hidden', !custom);
    this.customCard.classList.toggle('is-active', Boolean(custom));
    if (custom) {
      const area = num(bboxAreaKm2(custom.bbox), 2);
      this.customDetail.textContent = this.isHost ? t('{area} km² · click to edit', { area }) : `${area} km²`;
    }
  }

  update(lobby: LobbyPublic, localId: string): void {
    this.lobby = lobby;
    this.localId = localId;
    this.codeNode.textContent = lobby.code;
    const self = lobby.players.find((p) => p.id === localId);
    this.isHost = Boolean(self?.host);
    this.selfReady = Boolean(self?.ready);

    // A guest gets the host's own panel, read-only, rather than nothing: they watch the tour
    // and the region light up as the host picks them, the way a CS:GO lobby shows the map
    // vote. Everything that would change something is hidden or inert. DECISIONS.md §60.
    this.hostPanel.classList.toggle('is-readonly', !this.isHost);
    this.watchPanel.classList.toggle('is-hidden', this.isHost);
    this.readyButton.textContent = this.selfReady ? t('Not ready') : t("I'm ready");
    this.readyButton.classList.toggle('is-active', this.selfReady);

    clear(this.playerList);
    for (const player of lobby.players) {
      this.playerList.append(
        el(
          'li',
          { class: `player${player.id === localId ? ' is-self' : ''}` },
          el('span', { class: 'swatch', style: { background: hex(player.color) } }),
          el('span', { class: 'player-name' }, player.name),
          player.host && el('span', { class: 'tag tag-host' }, t('host')),
          player.ai && el('span', { class: 'tag' }, 'AI'),
          !player.connected && el('span', { class: 'tag' }, t('offline')),
          // An opponent is never what anyone is waiting for, so saying "ready" about one is
          // noise in the column that exists to show who is still deciding.
          !player.ai &&
            el(
              'span',
              { class: `player-ready${player.ready ? ' is-ready' : ''}` },
              player.ready ? t('ready') : t('waiting'),
            ),
        ),
      );
    }

    this.updateAiChips(lobby);

    if (lobby.tour) this.choosingTour = false;
    if (this.selectedTourId !== (lobby.tour?.id ?? null)) {
      this.selectedTourId = lobby.tour?.id ?? null;
      this.paintTourSelection();
    }
    this.intent = this.intentOf(lobby);
    for (const [intent, card] of Object.entries(this.intentCards)) {
      card.classList.toggle('is-active', intent === this.intent);
      card.setAttribute('aria-checked', String(intent === this.intent));
    }
    // Step 2 follows step 1: a tour says where every race happens, so it has its own list;
    // a race and a free drive both pick a place, and only a race has opponents.
    this.tourSection.classList.toggle('is-hidden', this.intent !== 'tour');
    this.regionSection.classList.toggle('is-hidden', this.intent === 'tour');
    this.aiSection.classList.toggle('is-hidden', this.intent === 'free');
    this.placesTitle.textContent = this.intent === 'free' ? t('Where are we driving?') : t('Where are we racing?');
    this.paintPlaces(lobby);
    this.paintWeather(lobby);

    const label = this.placeLabel(lobby);
    if (lobby.tour && lobby.mode === 'free') {
      this.regionNote.textContent = t('Free drive through the {name}: {stops}.', {
        name: lobby.tour.name,
        stops: lobby.tour.stops.map((s) => s.title).join(' → '),
      });
    } else if (lobby.tour) {
      this.regionNote.textContent = t('Racing the {name}: {stops}.', {
        name: lobby.tour.name,
        stops: lobby.tour.stops.map((s) => s.title).join(' → '),
      });
    } else if (label) {
      this.regionNote.textContent =
        lobby.mode === 'free' ? t('Free drive in {label}.', { label }) : t('Racing in {label}.', { label });
    } else {
      this.regionNote.textContent = this.isHost
        ? t('Pick a region below, then start the race.')
        : t('Waiting for the host to pick a region.');
    }
    if (lobby.weather) this.regionNote.textContent += ` ${serverText(lobby.weather.label)}.`;

    // Follows the server rather than the click. The region can now be chosen at the front
    // door, before this panel has ever been looked at, and it also arrives this way for a
    // guest who gets promoted to host — in both cases nothing here was ever pressed.
    for (const button of this.fixtureList.querySelectorAll<HTMLElement>('.fixture[data-fixture]')) {
      button.classList.toggle(
        'is-active',
        Boolean(lobby.region?.fixture) && button.dataset.fixture === lobby.region!.fixture,
      );
    }
    // An explanation of why the button did nothing stops being true the moment there is a region.
    if (lobby.region || lobby.tour) this.adjustNote.textContent = '';
    this.adjustButton.lastChild!.textContent = lobby.mode === 'free' ? t('Move the start') : t('Adjust the route');
    this.paintWatch(lobby);

    this.errorNode.textContent = serverText(lobby.error ?? '');

    const others = lobby.players.filter((p) => p.id !== localId);
    const everyoneReady = others.length === 0 || others.every((p) => p.ready || !p.connected);
    const generating = lobby.phase === 'generating';
    this.paintBuild(generating ? lobby.build : null);
    this.startButton.classList.toggle('is-pending', this.startBlocker() !== null);
    if (!this.startBlocker()) this.startNote.textContent = '';
    // "Start anyway" is gone for the tour case, and for the single-race case it now means
    // something slightly weaker than it used to: nobody is committed by it, because the
    // grid asks everyone again before the lights.
    this.cancelButton.classList.toggle('is-hidden', !generating || !this.isHost);
    this.startButton.lastChild!.textContent = generating
      ? t('Building the world…')
      : lobby.tour
        ? t('Start the {name}', { name: lobby.tour.name })
        : lobby.mode === 'free'
          ? t('Start free drive')
          : everyoneReady
          ? t('Start race')
          : t('Start anyway');
  }

  /**
   * Sends a chosen sky, filling in whatever was not just changed from what is already chosen
   * — or, switching from the real weather, from the real weather itself, so "Choose" starts
   * on the sky everyone is already looking at rather than jumping to noon.
   */
  private sendWeather(change: { hour?: number; kind?: WeatherKind }): void {
    const lobby = this.lobby;
    if (!lobby) return;
    const current = lobby.weatherChoice;
    const real = lobby.weather;
    const hour =
      change.hour ?? (current.real ? (real ? hourOf(real.localTime) : 12) : current.hour);
    const kind = change.kind ?? (current.real ? (real?.kind ?? 'clear') : current.kind);
    this.options.onWeatherChoice({ real: false, hour, kind });
  }

  private paintWeather(lobby: LobbyPublic): void {
    const choice = lobby.weatherChoice;
    this.weatherButtons.real.classList.toggle('is-selected', choice.real);
    this.weatherButtons.chosen.classList.toggle('is-selected', !choice.real);
    this.weatherCustom.classList.toggle('is-hidden', choice.real);
    if (!choice.real) {
      if (!this.draggingHour) {
        this.weatherHour.value = String(choice.hour);
        this.weatherClock.textContent = clockOf(choice.hour);
      }
      for (const [kind, chip] of this.weatherKinds) chip.classList.toggle('is-active', kind === choice.kind);
    }
    this.weatherHour.disabled = !this.isHost;
  }

  /** What the lobby is pointed at, in words: a stop or saved drawing by name, a fixture by title. */
  private placeLabel(lobby: LobbyPublic): string | null {
    if (!lobby.region) return null;
    if (lobby.regionTitle) return lobby.regionTitle;
    if (lobby.region.fixture) {
      return serverText(this.fixtures.find((f) => f.id === lobby.region!.fixture)?.title ?? lobby.region.fixture);
    }
    return t('a custom region');
  }

  /** Why Start cannot do anything yet, or null when it can. */
  private startBlocker(): string | null {
    const lobby = this.lobby;
    if (!lobby) return t('Still connecting to the lobby.');
    if (!this.isHost) {
      const host = lobby.players.find((p) => p.host);
      return host
        ? t("Only the host can start the race — that is {name}. Press “I'm ready” so they know you are.", { name: host.name })
        : t("Only the host can start the race. Press “I'm ready” so they know you are.");
    }
    if (lobby.phase === 'generating') return t('The world is already being built — it starts by itself when it is ready.');
    if (this.intent === 'tour' && !lobby.tour) return t('Pick a tour first — or choose Race or Free drive above.');
    if (!lobby.region && !lobby.tour) return t('Pick something to race first: a tour, one of the regions below, or draw your own.');
    return null;
  }

  /** Puts a sentence where the person who just pressed something is looking. */
  private say(node: HTMLElement, message: string): void {
    node.textContent = message;
    node.classList.add('is-error');
    nudge(node);
  }

  /** The guest's one-line account of what the host has picked. */
  private paintWatch(lobby: LobbyPublic): void {
    if (this.isHost) return;
    const host = lobby.players.find((p) => p.host);
    const who = host ? host.name : t('The host');
    if (lobby.tour) {
      this.watchTitle.textContent =
        lobby.mode === 'free'
          ? t('{who} picked a free drive through a tour: {name}', { who, name: lobby.tour.name })
          : t('{who} picked a tour: {name}', { who, name: lobby.tour.name });
      this.watchDetail.textContent = lobby.tour.stops.map((s) => s.title).join(' → ');
    } else if (lobby.region && (lobby.region.fixture || lobby.regionTitle)) {
      const fixture = this.fixtures.find((f) => f.id === lobby.region!.fixture);
      const title = this.placeLabel(lobby) ?? '';
      this.watchTitle.textContent =
        lobby.mode === 'free'
          ? t('{who} picked a free drive in {title}', { who, title })
          : t('{who} picked {title}', { who, title });
      this.watchDetail.textContent = serverText(fixture?.blurb ?? '');
    } else if (lobby.region) {
      const km2 = bboxAreaKm2(lobby.region.bbox);
      this.watchTitle.textContent =
        lobby.mode === 'free'
          ? t('{who} drew a region on the map for a free drive', { who })
          : t('{who} drew a region on the map', { who });
      this.watchDetail.textContent = `${num(km2, 2)} km²`;
    } else {
      this.watchTitle.textContent = t('{who} has not picked anything yet', { who });
      this.watchDetail.textContent = t('Whatever they choose lights up below.');
    }
  }

  /**
   * The world build, while it is happening.
   *
   * The elapsed clock ticks on the client rather than being sent, for two reasons: it is one
   * number that would otherwise need its own twice-a-second broadcast, and the two machines
   * do not agree on what time it is. It counts from when *this* client first saw the build,
   * which for a guest who joined mid-build understates it — and understating a wait you
   * arrived late to is the right way round.
   */
  private paintBuild(build: BuildProgress | null): void {
    this.buildPanel.classList.toggle('is-hidden', !build);
    if (!build) {
      if (this.buildTimer !== null) window.clearInterval(this.buildTimer);
      this.buildTimer = null;
      this.buildClock.update(null);
      return;
    }

    this.buildClock.update(build);
    this.tickElapsed();
    if (this.buildTimer === null) this.buildTimer = window.setInterval(() => this.tickElapsed(), 500);

    this.buildLabel.textContent = serverText(build.label);
    this.buildDetail.textContent = serverText(build.detail);
    this.buildDetail.classList.toggle('is-hidden', !build.detail);
    this.buildFill.style.width = `${Math.round(Math.max(0, Math.min(1, build.fraction)) * 100)}%`;
    // Only said once it is true, and it is the answer to the question the wait actually
    // raises: no, it will not be like this next time.
    this.buildNote.textContent = build.downloading ? t('First time here, so the streets are downloading. Next time is quick.') : '';
    this.buildNote.classList.toggle('is-hidden', !build.downloading);
  }

  private tickElapsed(): void {
    this.buildElapsed.textContent = formatElapsed(this.buildClock.elapsedSeconds());
    const left = remainingPhrase(this.buildClock.remainingSeconds());
    this.buildLeft.textContent = left;
    this.buildLeft.classList.toggle('is-hidden', !left);
  }


  /**
   * Reflects the count the server settled on, not the one that was clicked.
   *
   * The two differ whenever the grid is full: asking for seven opponents in a lobby of
   * three people gets you five, and the chip that lights up is the five. Showing the
   * request instead would have the UI quietly disagreeing with the field on the grid.
   */
  private updateAiChips(lobby: LobbyPublic): void {
    const humans = lobby.players.filter((p) => !p.ai).length;
    const room = Math.max(0, MAX_CARS - humans);
    for (const [count, chip] of this.aiChips.entries()) {
      chip.classList.toggle('is-active', count === lobby.aiCount);
      chip.disabled = count > room;
    }
    // Saying what the grid holds is also the explanation for why the higher chips are out:
    // eight cars start a race, however they are made up.
    this.aiNote.textContent =
      lobby.aiCount === 0
        ? t('No opponents — just the drivers in this lobby.')
        : t('{opponents} alongside {drivers}.', {
            opponents: plural(lobby.aiCount, '{n} opponent', '{n} opponents'),
            drivers: plural(humans, '{n} driver', '{n} drivers'),
          });
  }

  private async copyLink(): Promise<void> {
    const url = new URL(location.href);
    url.searchParams.set('code', this.codeNode.textContent ?? '');
    try {
      await navigator.clipboard.writeText(url.toString());
      this.copyButton.textContent = t('Copied!');
    } catch {
      // Clipboard is permission-gated; showing the URL is a fine fallback.
      this.copyButton.textContent = url.toString();
    }
    window.setTimeout(() => {
      this.copyButton.textContent = t('Copy link');
    }, 2200);
  }

  dispose(): void {}
}

/**
 * Case- and accent-insensitive, so searching "krakow" finds "Kraków".
 *
 * NFD splits a letter from its diacritic and the range below removes the diacritic, which
 * is the whole trick — and the reason it is done here rather than with `localeCompare`,
 * which answers a different question.
 */
/** 19.5 → "19:30". */
function clockOf(hour: number): string {
  const minutes = Math.round(hour * 60);
  return `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** "19:42" → 19.75, on the slider's quarter hours. */
function hourOf(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return Math.min(23.75, Math.round(((h || 0) + (m || 0) / 60) * 4) / 4);
}

function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
